/**
 * GeoAnalyzer — shared module for photo-location & video-location tools
 * Handles: Leaflet map, reverse geocoding, sun calculator, ELA, privacy, utilities
 */
(function() {
'use strict';

function GeoAnalyzer(opts) {
    opts = opts || {};
    this._locale = opts.locale || window.__LOCALE || 'en';
    this._map = null;
    this._marker = null;
    this._baseLayer = null;   // the shared basemap (js/map-tiles.js) — ONE layer
    this._satLayer = null;
    this._isSat = false;
    this._batchMarkers = [];
    this._geoCache = {};
}

/* ═══════════════════════════════════════
   MAP (Leaflet; the shared basemap — dark/light after the site theme; Esri satellite)
   ═══════════════════════════════════════ */

GeoAnalyzer.prototype.initMap = function(el, lat, lon, zoom) {
    if (this._map) {
        this._map.setView([lat, lon], zoom || 15);
        // The container may have changed size since the last file (the desk
        // and the map swap places), and Leaflet only lays tiles out for the
        // size it knew at init — the symptom is a blank strip along one edge.
        var m = this._map;
        requestAnimationFrame(function() { m.invalidateSize(false); });
        return;
    }
    this._map = L.map(el, { center: [lat, lon], zoom: zoom || 15, zoomControl: true, attributionControl: true });
    // The shared basemap follows the site theme by itself and falls back on its
    // own (second host, then raster). It hands back ONE layer, so the satellite
    // switch below takes it off the map and puts it back.
    if (window.__mapTiles) {
        this._baseLayer = window.__mapTiles.base(L, this._map, null, { style: 'auto', control: false, opts: { maxZoom: 19 } });
    } else if (window.__basemapFallback) {
        this._baseLayer = window.__basemapFallback(L, this._map, '');
    }
    var self = this;

    // First layout happens while the panel is still being revealed, so the
    // initial measurement is short. Re-measure once the browser has painted,
    // and keep measuring while the column is resized (the map sits in a grid
    // track that reflows when the facts card grows).
    requestAnimationFrame(function() { self._map.invalidateSize(false); });
    if (typeof ResizeObserver !== 'undefined') {
        this._sizeObs = new ResizeObserver(function() {
            if (self._map) self._map.invalidateSize(false);
        });
        this._sizeObs.observe(el);
    }
};

GeoAnalyzer.prototype.switchToStreet = function() {
    if (!this._map || !this._isSat) return;
    this._isSat = false;
    if (this._satLayer) this._map.removeLayer(this._satLayer);
    if (this._baseLayer && !this._map.hasLayer(this._baseLayer)) this._baseLayer.addTo(this._map);
};

GeoAnalyzer.prototype.switchToSatellite = function() {
    if (!this._map || this._isSat) return;
    this._isSat = true;
    if (!this._satLayer) {
        this._satLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 19, attribution: 'Imagery &copy; Esri'
        });
    }
    if (this._baseLayer) this._map.removeLayer(this._baseLayer);
    this._satLayer.addTo(this._map);
};

GeoAnalyzer.prototype.updateMarker = function(lat, lon) {
    if (!this._map) return;
    if (this._marker) this._map.removeLayer(this._marker);
    var icon = L.divIcon({
        className: '',
        html: '<div style="width:14px;height:14px;background:var(--accent);border:2px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>',
        iconSize: [14, 14], iconAnchor: [7, 7]
    });
    this._marker = L.marker([lat, lon], { icon: icon }).addTo(this._map);
    this._map.setView([lat, lon], 15);
};

GeoAnalyzer.prototype.addBatchMarker = function(lat, lon, label, popupText) {
    if (!this._map) return null;
    var icon = L.divIcon({
        className: '',
        html: '<div style="width:24px;height:24px;background:var(--accent);border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)">' + GeoAnalyzer.escHtml(String(label)) + '</div>',
        iconSize: [24, 24], iconAnchor: [12, 12]
    });
    var m = L.marker([lat, lon], { icon: icon }).addTo(this._map);
    if (popupText) m.bindPopup('<b>' + GeoAnalyzer.escHtml(popupText) + '</b>');
    this._batchMarkers.push(m);
    return m;
};

GeoAnalyzer.prototype.clearBatchMarkers = function() {
    var map = this._map;
    this._batchMarkers.forEach(function(m) { if (map) map.removeLayer(m); });
    this._batchMarkers = [];
};

GeoAnalyzer.prototype.fitBounds = function(coords) {
    if (!this._map || coords.length < 2) return;
    this._map.fitBounds(coords, { padding: [30, 30] });
};

GeoAnalyzer.prototype.removeMarker = function() {
    if (this._marker && this._map) this._map.removeLayer(this._marker);
    this._marker = null;
};

/* ═══════════════════════════════════════
   GPS TRACK — polyline route + start/end + video-synced playhead
   (used by video-location for GoPro / DJI / dashcam multi-point tracks)
   ═══════════════════════════════════════ */

GeoAnalyzer.prototype.drawTrack = function(points, opts) {
    if (!this._map || !points || !points.length) return;
    opts = opts || {};
    this.clearTrack();
    var self = this;
    var latlngs = points.map(function(p){ return [p.lat, p.lon]; });
    // Casing under the route for contrast on both map themes.
    this._trackCasing = L.polyline(latlngs, { color: 'rgba(0,0,0,0.35)', weight: 7, opacity: 0.5, lineJoin: 'round', lineCap: 'round' }).addTo(this._map);
    this._trackLine = L.polyline(latlngs, { color: opts.color || '#38bdf8', weight: 4, opacity: 0.95, lineJoin: 'round', lineCap: 'round' }).addTo(this._map);
    var dot = function(ll, bg, ring){
        return L.marker(ll, { icon: L.divIcon({ className: '', html: '<div style="width:16px;height:16px;background:'+bg+';border:2.5px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,.45)'+(ring?';outline:'+ring:'')+'"></div>', iconSize: [16,16], iconAnchor: [8,8] }) });
    };
    if (latlngs.length > 1) {
        this._trackStart = dot(latlngs[0], '#22c55e').addTo(this._map);
        this._trackEnd = dot(latlngs[latlngs.length-1], '#ef4444').addTo(this._map);
    }
    this._playhead = L.marker(latlngs[0], { zIndexOffset: 1000, icon: L.divIcon({ className: '', html: '<div style="width:14px;height:14px;background:#fff;border:3px solid #38bdf8;border-radius:50%;box-shadow:0 0 0 4px rgba(56,189,248,.35)"></div>', iconSize: [14,14], iconAnchor: [7,7] }) }).addTo(this._map);
    try { this._map.fitBounds(this._trackLine.getBounds(), { padding: [34,34], maxZoom: 17 }); } catch(e) {}
};

GeoAnalyzer.prototype.movePlayhead = function(lat, lon) {
    if (this._playhead) this._playhead.setLatLng([lat, lon]);
};

GeoAnalyzer.prototype.clearTrack = function() {
    var m = this._map; if (!m) return;
    ['_trackCasing','_trackLine','_trackStart','_trackEnd','_playhead'].forEach(function(k){
        if (this[k]) { m.removeLayer(this[k]); this[k] = null; }
    }, this);
};

/* ═══════════════════════════════════════
   REVERSE GEOCODING (Nominatim)
   ═══════════════════════════════════════ */

/* Resolves to { full, line1, line2, cc } — `full` is Nominatim's display_name,
 * `line1`/`line2` the short human form built by GeoLocate.shortAddress.
 *
 * Nominatim is a volunteer service under a strict usage policy, so results are
 * cached per rounded coordinate and requested once. The previous version also
 * set a User-Agent header, which browsers refuse to send (it is a forbidden
 * header) — it looked like compliance and was doing nothing. */
GeoAnalyzer.prototype.reverseGeocode = function(lat, lon) {
    var key = lat.toFixed(4) + ',' + lon.toFixed(4);
    if (this._geoCache[key]) return Promise.resolve(this._geoCache[key]);
    var self = this;
    return fetch('https://nominatim.openstreetmap.org/reverse?lat=' + lat + '&lon=' + lon +
                 '&format=json&addressdetails=1&zoom=18&accept-language=' + encodeURIComponent(this._locale))
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
            var out = (window.GeoLocate && data)
                ? window.GeoLocate.shortAddress(data)
                : { full: (data && data.display_name) || '', line1: '', line2: '', cc: '' };
            self._geoCache[key] = out;
            return out;
        }).catch(function() { return { full: '', line1: '', line2: '', cc: '' }; });
};

/* ═══════════════════════════════════════
   SUN CALCULATOR (inline SunCalc)
   ═══════════════════════════════════════ */

/* PERIHELION is the Earth's argument of perihelion, 102.9372°. The half turn
 * that converts the Sun's ecliptic longitude from the Earth's is the separate
 * `+ Math.PI` below.
 *
 * ⚠️ This constant read 282.9372 (= 102.9372 + 180) until 2026-08-02, so the
 * half turn was applied TWICE and every result came back with the Sun on the
 * opposite side of the sky. Measured against the NOAA solar calculator:
 * Greenwich at solar noon on the summer solstice returned an elevation of
 * −62.0° (night) instead of +62.0°, and an azimuth of 359° (north) instead of
 * 180° (south). Both location tools shipped a "sun position at capture" panel
 * on top of it. Nobody reported it because both tools are unlisted and have
 * never had a single visitor — which is exactly why a number has to be checked
 * against an external reference rather than against the absence of complaints. */
var PERIHELION = 102.9372;

GeoAnalyzer.sunPosition = function(date, lat, lng) {
    var rad = Math.PI / 180, e = rad * 23.4397;
    var d = (date.getTime() / 86400000) - 10957.5;
    var M = rad * (357.5291 + 0.98560028 * d);
    var C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2*M) + 0.0003 * Math.sin(3*M));
    var L = M + C + rad * PERIHELION + Math.PI;
    var dec = Math.asin(Math.sin(e) * Math.sin(L));
    var ra = Math.atan2(Math.sin(L) * Math.cos(e), Math.cos(L));
    var lw = rad * -lng, phi = rad * lat;
    var H = rad * (280.16 + 360.9856235 * d) - lw - ra;
    var alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
    var az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
    return { azimuth: (az / rad + 180) % 360, elevation: alt / rad };
};

GeoAnalyzer.sunTimes = function(date, lat, lng) {
    var rad = Math.PI / 180, e = rad * 23.4397;
    var d = (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000) - 10957.5;
    var M = rad * (357.5291 + 0.98560028 * d);
    var C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2*M));
    var L = M + C + rad * PERIHELION + Math.PI;   // see the note on PERIHELION
    var dec = Math.asin(Math.sin(e) * Math.sin(L));
    var phi = rad * lat;
    var cosH = (Math.sin(rad * -0.833) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec));
    if (cosH > 1 || cosH < -1) return null;   // polar day or polar night
    /* The solar transit has to be pinned to a WHOLE solar cycle — the integer
     * number of days since J2000 nearest to the requested date — and J2000 is
     * reckoned from noon, so a midnight-UTC `d` carries a .5 fraction that must
     * be rounded away.
     *
     * ⚠️ This line used the raw `d` until 2026-08-02, i.e. it placed the
     * transit at local MIDNIGHT. Measured: Greenwich on the 2026 summer
     * solstice came back as sunrise 15:43Z / sunset 08:21Z — both exactly 12
     * hours from the true 03:43Z / 20:21Z. Sunrise, sunset and the golden-hour
     * readout on both location tools were built on it. */
    var cycle = Math.round(d - 0.0009 - (-lng / 360));
    var Jn = 0.0009 + (-lng / 360) + cycle;
    var Jt = 2451545 + Jn + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2*L);
    var w = Math.acos(cosH) / rad;
    var rise = new Date((Jt - w/360 - 2440587.5) * 86400000);
    var set  = new Date((Jt + w/360 - 2440587.5) * 86400000);
    return { sunrise: rise, sunset: set };
};

/* ═══════════════════════════════════════
   SHADOW → LATITUDE
   Shadows fall directly away from the sun, so a shadow bearing fixes the solar
   azimuth. With the capture instant and a longitude band (from the file's UTC
   offset) already known, the only remaining unknown is latitude — and solar
   azimuth varies enough with latitude to pin it to one or two narrow bands.
   This is the standard shadow-geolocation method done on numbers instead of a
   protractor, and it is the one useful thing left when a file has a timestamp
   but no coordinates.

   Lives here, not in a tool, because photo-location and video-location both
   need it and a second copy would drift.
   ═══════════════════════════════════════ */
GeoAnalyzer.shadowLatitudes = function(date, lon, shadowAzimuth, opts) {
    opts = opts || {};
    var tol = opts.toleranceDeg || 6;
    var step = opts.stepDeg || 0.5;
    var sunAz = ((shadowAzimuth % 360) + 540) % 360;   // shadow → sun: +180°
    var hits = [];
    for (var lat = -85; lat <= 85; lat += step) {
        var p = GeoAnalyzer.sunPosition(date, lat, lon);
        // Below the horizon there is no shadow to point anywhere, so those
        // latitudes are not candidates — they are impossibilities.
        if (p.elevation <= 0) continue;
        var d = Math.abs(((p.azimuth - sunAz + 540) % 360) - 180);
        if (d < tol) hits.push({ lat: lat, elevation: p.elevation, azimuth: p.azimuth });
    }
    if (!hits.length) return [];
    // Contiguous runs are ONE band each. Reporting 40 individual half-degree
    // hits as 40 answers would be technically true and practically useless.
    var bands = [], cur = [hits[0]];
    for (var i = 1; i < hits.length; i++) {
        if (hits[i].lat - cur[cur.length - 1].lat <= step * 1.5) cur.push(hits[i]);
        else { bands.push(cur); cur = [hits[i]]; }
    }
    bands.push(cur);
    return bands.map(function(b) {
        var mid = b[Math.floor(b.length / 2)];
        return { from: b[0].lat, to: b[b.length - 1].lat, elevation: mid.elevation };
    });
};

/* The dial the user turns to report where the shadows point. Drawn at device
   scale (the canvas is sized 2× its CSS box) so it stays crisp on phones. */
GeoAnalyzer.drawShadowDial = function(canvas, azimuth, labels) {
    var ctx = canvas.getContext('2d');
    var w = canvas.width, h = canvas.height, cx = w / 2, cy = h / 2, r = w / 2 - 26;
    var light = document.documentElement.dataset.theme === 'light';
    var ink = light ? 'rgba(0,0,0,.55)' : 'rgba(255,255,255,.6)';
    var faint = light ? 'rgba(0,0,0,.16)' : 'rgba(255,255,255,.18)';
    var L = labels || { n: 'N', s: 'S', e: 'E', w: 'W' };
    ctx.clearRect(0, 0, w, h);
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = faint; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = ink; ctx.font = '600 20px system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(L.n, cx, cy - r - 13); ctx.fillText(L.s, cx, cy + r + 13);
    ctx.fillText(L.e, cx + r + 14, cy); ctx.fillText(L.w, cx - r - 14, cy);
    for (var i = 0; i < 360; i += 15) {
        var a = (i - 90) * Math.PI / 180, len = i % 45 === 0 ? 12 : 6;
        ctx.beginPath();
        ctx.moveTo(cx + (r - len) * Math.cos(a), cy + (r - len) * Math.sin(a));
        ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
        ctx.strokeStyle = faint; ctx.lineWidth = 2; ctx.stroke();
    }
    var ar = (azimuth - 90) * Math.PI / 180;
    var tipX = cx + (r - 16) * Math.cos(ar), tipY = cy + (r - 16) * Math.sin(ar);
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(tipX, tipY);
    ctx.strokeStyle = '#38bdf8'; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.stroke();
    ctx.beginPath(); ctx.arc(tipX, tipY, 9, 0, Math.PI * 2); ctx.fillStyle = '#38bdf8'; ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, 6, 0, Math.PI * 2); ctx.fillStyle = ink; ctx.fill();
};

/* Wire a canvas as a draggable + keyboard-operable bearing dial. Returns a
   setter so the caller can drive it programmatically. */
GeoAnalyzer.bindShadowDial = function(canvas, labels, onChange) {
    var az = 0, dragging = false, touched = false;
    function paint() {
        GeoAnalyzer.drawShadowDial(canvas, az, labels);
        canvas.setAttribute('aria-valuenow', String(Math.round(az)));
        // The needle has to start somewhere, and for most instants that
        // arbitrary bearing has no solution — so the untouched dial would
        // greet the user with "no latitude fits", which reads as a broken
        // tool rather than as an unanswered question. `touched` lets the
        // caller show the instruction until the user has actually said
        // something.
        onChange(az, !touched);
    }
    function angleAt(ev) {
        var r = canvas.getBoundingClientRect();
        var x = (ev.clientX - r.left) - r.width / 2;
        var y = (ev.clientY - r.top) - r.height / 2;
        return (Math.atan2(x, -y) * 180 / Math.PI + 360) % 360;
    }
    canvas.addEventListener('pointerdown', function(e) {
        dragging = true; touched = true; canvas.setPointerCapture(e.pointerId); az = angleAt(e); paint();
    });
    canvas.addEventListener('pointermove', function(e) { if (dragging) { az = angleAt(e); paint(); } });
    canvas.addEventListener('pointerup', function() { dragging = false; });
    canvas.addEventListener('pointercancel', function() { dragging = false; });
    canvas.addEventListener('keydown', function(e) {
        var step = e.shiftKey ? 15 : 5;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { az = (az - step + 360) % 360; touched = true; e.preventDefault(); paint(); }
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { az = (az + step) % 360; touched = true; e.preventDefault(); paint(); }
    });
    // The dial is drawn with theme-dependent ink, so it has to be repainted
    // when the user flips the theme — otherwise it goes invisible.
    new MutationObserver(paint).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    paint();
    return function(v) { az = ((v % 360) + 360) % 360; touched = true; paint(); };
};

GeoAnalyzer.drawCompass = function(canvas, azimuth) {
    var ctx = canvas.getContext('2d');
    var w = canvas.width, h = canvas.height, cx = w/2, cy = h/2, r = 65;
    ctx.clearRect(0, 0, w, h);

    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI*2);
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke();

    ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('N', cx, cy - r - 6);
    ctx.fillText('S', cx, cy + r + 14);
    ctx.fillText('E', cx + r + 10, cy + 4);
    ctx.fillText('W', cx - r - 10, cy + 4);

    for (var i = 0; i < 360; i += 30) {
        var rad2 = (i - 90) * Math.PI / 180;
        var len = i % 90 === 0 ? 8 : 4;
        ctx.beginPath();
        ctx.moveTo(cx + (r - len) * Math.cos(rad2), cy + (r - len) * Math.sin(rad2));
        ctx.lineTo(cx + r * Math.cos(rad2), cy + r * Math.sin(rad2));
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.lineWidth = 1; ctx.stroke();
    }

    if (azimuth == null) return;
    var sunRad = (azimuth - 90) * Math.PI / 180;
    var sx = cx + (r - 15) * Math.cos(sunRad);
    var sy = cy + (r - 15) * Math.sin(sunRad);

    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(sx, sy);
    ctx.strokeStyle = 'rgba(245,158,11,0.6)'; ctx.lineWidth = 2; ctx.stroke();

    ctx.beginPath(); ctx.arc(sx, sy, 8, 0, Math.PI*2);
    ctx.fillStyle = '#f59e0b'; ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('\u2600', sx, sy);
};

/* ═══════════════════════════════════════
   ELA (Error Level Analysis)
   ═══════════════════════════════════════ */

GeoAnalyzer.performELA = function(img, canvas, quality, sensitivity) {
    var w = Math.min(img.naturalWidth, 1200);
    var scale = w / img.naturalWidth;
    var h = Math.round(img.naturalHeight * scale);
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var dataUrl = canvas.toDataURL('image/jpeg', quality / 100);
    var recomp = new Image();
    recomp.onload = function() {
        var c2 = document.createElement('canvas');
        c2.width = w; c2.height = h;
        var ctx2 = c2.getContext('2d', { willReadFrequently: true });
        ctx2.drawImage(recomp, 0, 0, w, h);

        var orig = ctx.getImageData(0, 0, w, h);
        var comp = ctx2.getImageData(0, 0, w, h);
        var diff = ctx.createImageData(w, h);

        for (var i = 0; i < orig.data.length; i += 4) {
            diff.data[i]   = Math.min(255, Math.abs(orig.data[i]   - comp.data[i])   * sensitivity);
            diff.data[i+1] = Math.min(255, Math.abs(orig.data[i+1] - comp.data[i+1]) * sensitivity);
            diff.data[i+2] = Math.min(255, Math.abs(orig.data[i+2] - comp.data[i+2]) * sensitivity);
            diff.data[i+3] = 255;
        }
        ctx.putImageData(diff, 0, 0);
    };
    recomp.src = dataUrl;
};

/* ═══════════════════════════════════════
   PRIVACY RISK SCAN
   ═══════════════════════════════════════ */

GeoAnalyzer.scanRisks = function(exif, gps, T) {
    var risks = [];
    if (gps) risks.push({ level: 'high', label: T.risk_high, text: T.risk_gps, value: gps.lat.toFixed(6) + ', ' + gps.lon.toFixed(6) });
    if (exif.SerialNumber || exif.InternalSerialNumber) risks.push({ level: 'med', label: T.risk_medium, text: T.risk_serial, value: exif.SerialNumber || exif.InternalSerialNumber });
    if (exif.Artist || exif.Creator || exif.XPAuthor || exif.Copyright) risks.push({ level: 'med', label: T.risk_medium, text: T.risk_owner, value: exif.Artist || exif.Creator || exif.XPAuthor || exif.Copyright });
    if (exif.DateTimeOriginal) risks.push({ level: 'low', label: T.risk_low, text: T.risk_datetime, value: GeoAnalyzer.formatDate(exif.DateTimeOriginal) });
    if (exif.Make || exif.Model) risks.push({ level: 'low', label: T.risk_low, text: T.risk_camera, value: [exif.Make, exif.Model].filter(Boolean).join(' ') });
    if (exif.Software) risks.push({ level: 'low', label: T.risk_low, text: T.risk_software, value: exif.Software });
    return risks;
};

/* ═══════════════════════════════════════
   FORENSIC CONSISTENCY CHECKS
   ═══════════════════════════════════════ */

GeoAnalyzer.checkConsistency = function(exif, T) {
    var results = [];

    // Date consistency
    var d1 = exif.DateTimeOriginal, d2 = exif.ModifyDate;
    if (d1 && d2) {
        var t1 = new Date(d1).getTime(), t2 = new Date(d2).getTime();
        results.push({ key: T.consistency_dates, status: t2 < t1 ? 'warning' : 'ok', text: t2 < t1 ? T.forensic_warning : T.forensic_ok });
    } else if (d1 || exif.CreateDate) {
        results.push({ key: T.consistency_dates, status: 'ok', text: T.forensic_ok });
    } else {
        results.push({ key: T.consistency_dates, status: 'info', text: T.forensic_na });
    }

    // Software chain
    var sw = exif.Software || '';
    var isEditor = /photoshop|gimp|lightroom|snapseed|afterlight|vsco/i.test(sw);
    if (sw) {
        results.push({ key: T.consistency_software, status: isEditor ? 'warning' : 'ok', text: (isEditor ? T.forensic_warning : T.forensic_ok) + ' (' + sw + ')' });
    } else {
        results.push({ key: T.consistency_software, status: 'info', text: T.forensic_na });
    }

    // Dimension consistency
    var w1 = exif.ImageWidth, h1 = exif.ImageHeight, w2 = exif.ExifImageWidth, h2 = exif.ExifImageHeight;
    if (w1 && w2 && h1 && h2) {
        var match = (w1 === w2 && h1 === h2);
        results.push({ key: T.consistency_dimensions, status: match ? 'ok' : 'warning', text: match ? T.forensic_ok : T.forensic_warning });
    } else {
        results.push({ key: T.consistency_dimensions, status: 'info', text: T.forensic_na });
    }

    return results;
};

/* ═══════════════════════════════════════
   IMAGE METADATA STRIPPING (Canvas redraw)
   ═══════════════════════════════════════ */

GeoAnalyzer.stripImage = function(file, type) {
    return new Promise(function(resolve, reject) {
        var img = new Image();
        img.onload = function() {
            var canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            var mime = type === 'image/png' ? 'image/png' : 'image/jpeg';
            canvas.toBlob(function(blob) { blob ? resolve(blob) : reject(new Error('toBlob failed')); }, mime, 0.95);
            URL.revokeObjectURL(img.src);
        };
        img.onerror = function() { URL.revokeObjectURL(img.src); reject(new Error('Image load failed')); };
        img.src = URL.createObjectURL(file);
    });
};

/* ═══════════════════════════════════════
   NOTE: MP4 video GPS / metadata parsing lives in video-meta.js.
   The old GeoAnalyzer.parseMP4GPS brute-force byte scan was removed —
   it only found one GPS shape, could false-positive inside mdat, and
   never read capture date / codecs / device. video-meta.js does a real
   ISO-BMFF box-tree parse (udta/©xyz, meta/keys+ilst, loci, mvhd).
   ═══════════════════════════════════════ */

/* ═══════════════════════════════════════
   UTILITIES (static methods)
   ═══════════════════════════════════════ */

GeoAnalyzer.formatBytes = function(b) {
    if (b === 0) return '0 B';
    var k = 1024, s = ['B','KB','MB','GB'];
    var i = Math.floor(Math.log(b) / Math.log(k));
    return (b / Math.pow(k, i)).toFixed(1) + ' ' + s[i];
};

GeoAnalyzer.formatDate = function(d) {
    if (!d) return null;
    if (d instanceof Date) return d.toLocaleString();
    return String(d);
};

GeoAnalyzer.formatTime = function(d) {
    if (!d || !(d instanceof Date)) return '';
    return d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0');
};

GeoAnalyzer.formatShutter = function(v) {
    if (!v) return null;
    if (v >= 1) return v + 's';
    return '1/' + Math.round(1/v) + 's';
};

GeoAnalyzer.formatFlash = function(v, T) {
    if (v == null) return null;
    if (typeof v === 'object' && v.fired != null) return v.fired ? T.flash_fired : T.flash_not_fired;
    if (v === 0 || v === 16) return T.flash_not_fired;
    if (v === 1 || v === 5 || v === 7) return T.flash_fired;
    return String(v);
};

GeoAnalyzer.formatMetering = function(v) {
    if (v == null) return null;
    var map = { 1: 'Average', 2: 'Center-weighted', 3: 'Spot', 4: 'Multi-spot', 5: 'Multi-segment', 6: 'Partial' };
    return map[v] || String(v);
};

GeoAnalyzer.formatExposureMode = function(v) {
    if (v == null) return null;
    var map = { 0: 'Auto', 1: 'Manual', 2: 'Auto bracket' };
    return map[v] || String(v);
};

GeoAnalyzer.formatColorSpace = function(v) {
    if (v == null) return null;
    if (v === 1) return 'sRGB';
    if (v === 65535) return 'Uncalibrated';
    return String(v);
};

GeoAnalyzer.formatDuration = function(v) {
    if (!v) return null;
    var s = Math.round(v);
    var m = Math.floor(s / 60); s = s % 60;
    var h = Math.floor(m / 60); m = m % 60;
    if (h > 0) return h + ':' + m.toString().padStart(2,'0') + ':' + s.toString().padStart(2,'0');
    return m + ':' + s.toString().padStart(2,'0');
};

GeoAnalyzer.escHtml = function(s) {
    if (!s) return '';
    var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(s).replace(/[&<>"']/g, function(m) { return map[m]; });
};

GeoAnalyzer.statusIcon = function(status) {
    if (status === 'ok') return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';
    if (status === 'warning') return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
};

GeoAnalyzer.toast = function(msg) {
    if (window.__toast) { window.__toast(msg, 'success'); return; }
    var el = document.getElementById('plToast') || document.getElementById('vlToast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(el._t);
    el._t = setTimeout(function() { el.classList.remove('show'); }, 2000);
};

GeoAnalyzer.copyText = function(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
    }
    // Fallback for older browsers / non-HTTPS
    return new Promise(function(resolve, reject) {
        try {
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            resolve();
        } catch(e) { reject(e); }
    });
};

/* Export */
window.GeoAnalyzer = GeoAnalyzer;

})();
